import {
  Columns,
  Icon,
  Radio,
  Stack,
  Text,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import React, { useState, useEffect } from 'react'
import { ApiProduct } from '@vend/types'
import { trackCustomHeapEvent } from '@vend/utilities'
import {
  HEAP_LINE_ITEM_FULFILMENT_DECISION_ACTION,
  HEAP_PACKAGING_BREAKDOWN_OPTION_OBJECT,
} from '../../../sale-model.constant'

import { ShouldFulfillOpt } from './LineItemFulfillmentContext'

export const TContext = 'Line item fulfilment popover'
export const TestId = 'lif-decision-view'

type Props = {
  decision: ShouldFulfillOpt
  productQuantity: number
  productName: string
  onSelect: (opt: ShouldFulfillOpt, isBreakdownSelected: boolean) => void
  canShowBreakdownOption?: boolean
  outerProduct?: ApiProduct
}

// Internal enum for display purposes
enum DisplayOption {
  CONTINUE_ANYWAY = 'continue_anyway',
  BREAKDOWN = 'break_down',
  FULFIL_LATER = 'fulfil_later',
}

export const LineItemFulfilmentDecisionView: React.FC<Props> = ({
  decision,
  productQuantity,
  productName,
  onSelect,
  canShowBreakdownOption = false,
  outerProduct,
}: Props) => {
  const t = useT()

  const [
    selectedDisplayOption,
    setSelectedDisplayOption,
  ] = useState<DisplayOption>(
    decision === ShouldFulfillOpt.YES
      ? DisplayOption.FULFIL_LATER
      : DisplayOption.CONTINUE_ANYWAY
  )

  const handleOptionSelect = (displayOption: DisplayOption) => {
    setSelectedDisplayOption(displayOption)

    onSelect(
      displayOption === DisplayOption.FULFIL_LATER
        ? ShouldFulfillOpt.YES
        : ShouldFulfillOpt.NO,
      displayOption === DisplayOption.BREAKDOWN
    )
  }

  useEffect(() => {
    if (canShowBreakdownOption) {
      trackCustomHeapEvent(
        {
          action: HEAP_LINE_ITEM_FULFILMENT_DECISION_ACTION,
          object: HEAP_PACKAGING_BREAKDOWN_OPTION_OBJECT,
        },
        {
          productName,
          outerProductName: outerProduct?.name || '',
          productQuantity,
        }
      )
    }
    // intentionally omit dependencies to track this event only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack space={6} data-testid={TestId}>
      <Columns space={2}>
        <Text type="label">
          <Icon
            icon="fa-exclamation-triangle"
            className="wr-input-error-icon"
          />
        </Text>
        <Text type="label">
          <h3>
            {t(
              '{productQuantity, plural,' +
                'one {There’s {productQuantity} available of “{productName}”}' +
                'other {There’s {productQuantity} available of “{productName}”}}',
              { _context: TContext, productQuantity, productName }
            )}
          </h3>
        </Text>
      </Columns>

      <Stack space={2}>
        <Text type="label">
          <T _str="What do you want to do?" _context={TContext} />
        </Text>
        <Stack space={3}>
          <Radio
            className="vd-pl0"
            name="decision"
            label={t('Continue anyway', { _context: TContext })}
            value={DisplayOption.CONTINUE_ANYWAY}
            checked={selectedDisplayOption === DisplayOption.CONTINUE_ANYWAY}
            onChange={() => handleOptionSelect(DisplayOption.CONTINUE_ANYWAY)}
            data-track="continue-anyway-option"
          />
          {canShowBreakdownOption && (
            <Radio
              className="vd-pl0"
              name="decision"
              label={t('Break down from "{outerProductName}"', {
                _context: TContext,
                outerProductName:
                  outerProduct?.name ??
                  t('outer product', {
                    _context: 'outer product that is broken down',
                  }),
              })}
              value={DisplayOption.BREAKDOWN}
              checked={selectedDisplayOption === DisplayOption.BREAKDOWN}
              onChange={() => handleOptionSelect(DisplayOption.BREAKDOWN)}
              data-track="breakdown-option"
            />
          )}
          <Radio
            className="vd-pl0"
            name="decision"
            label={t('Fulfill later', { _context: TContext })}
            value={DisplayOption.FULFIL_LATER}
            checked={selectedDisplayOption === DisplayOption.FULFIL_LATER}
            onChange={() => handleOptionSelect(DisplayOption.FULFIL_LATER)}
            data-track="fulfil-later-option"
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
